import React, { useState } from "react";
import { Box, Divider, Grid, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import InputCst from "@components/InputCst/InputCst";
import SelectCst from "@components/InputCst/SelectCst";
import InputChipsCst from "@components/InputCst/InpuctChipsCst";
import BadgePickerCst from "@components/BadgePickerCst";
import CertificatePickerCst from "@components/CertificatePickerCst";
import StakeholdersFieldCst from "@components/StakeholdersFieldCst";
import { MediaType } from "src/interfaces/Media";
import { WizardStepProps } from "@components/WizardCst/types";
import MultiAssociationField from "@pages/Contents/V2/Corporate/Catalog/AddActivity/steps/AdditionalInfoStep/sections/MultiAssociationField";
import UsefulLinksField from "@pages/Contents/V2/Corporate/Catalog/AddActivity/steps/AdditionalInfoStep/sections/UsefulLinksField";
import AssociationFieldPlaceholder from "@pages/Contents/V2/Corporate/Catalog/AddActivity/steps/MainInfoStep/sections/AssociationFieldPlaceholder";
import { ComplexityLevel } from "../../Course/types";
import { PathWizardData } from "../types";
import { COMPLEXITY_OPTIONS } from "../config/pathOptions";
import { PERMISSIONS, usePermissions } from "@utils/permissions";

const SectionDivider: React.FC = () => <Divider sx={{ opacity: 0.6 }} />;

const SectionHeader: React.FC<{
  title: string;
  description?: string;
}> = ({ title, description }) => {
  const theme = useTheme();
  return (
    <Box>
      <Typography
        sx={{
          fontSize: "20px",
          fontWeight: 700,
          color: theme.palette.text.primary,
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography
          sx={{
            fontSize: "14px",
            color: theme.palette.text.primary,
            mt: 0.5,
            lineHeight: 1.5,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

const AdditionalInfoStep: React.FC<WizardStepProps<PathWizardData>> = ({
  data,
  setData,
  errors,
}) => {
  const { t } = useTranslation("wizard-path");
  const { t: tCert } = useTranslation("certificate-picker");
  const { t: tBadge } = useTranslation("badge-picker");
  const { t: tPicker } = useTranslation("media-library-picker");

  const errMsg = (key: string): string =>
    errors[key] ? t(`errors.${errors[key]}`) || "" : "";

  const [internalCertOpen, setInternalCertOpen] = useState(false);
  const [signatureCertOpen, setSignatureCertOpen] = useState(false);
  const [badgeOpen, setBadgeOpen] = useState(false);

  // P58: badge visibile con fe.badge.template.read, modificabile con
  // fe.badge.template.write (come il wizard attività).
  const { can } = usePermissions();
  const canViewBadge = can(PERMISSIONS.badge.read);
  const canEditBadge = can(PERMISSIONS.badge.write);

  const toSelections = (values: { id: number | string; label?: string }[]) =>
    values.map((v) => ({ id: Number(v.id), label: v.label || `#${v.id}` }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionHeader
          title={t("sections.tools.title")}
          description={t("sections.tools.description") || undefined}
        />

        <MultiAssociationField
          label={t("fields.didactical-material")}
          values={data.didactical_material}
          onChange={(next) => setData({ didactical_material: next })}
          addLabel={t("common.add") || "Aggiungi"}
          emptyLabel={
            t("common.empty-didactical-material") ||
            "Nessun materiale didattico aggiunto"
          }
          emptyVariant="card"
          mediaType={MediaType.DIDACTICAL_MATERIAL}
          pickerTitle={tPicker("title-material") || ""}
        />

        <UsefulLinksField
          values={data.useful_links}
          onChange={(next) => setData({ useful_links: next })}
        />

        <StakeholdersFieldCst
          title={t("sections.stakeholders.title")}
          description={t("sections.stakeholders.description") || undefined}
          addLabel={t("common.add") || "Aggiungi"}
          values={data.stakeholders || []}
          onChange={(next) => setData({ stakeholders: next })}
        />
      </Box>

      <SectionDivider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionHeader
          title={t("sections.certificates.title")}
          description={t("sections.certificates.description") || undefined}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <MultiAssociationField
              label={t("fields.certificate-internal")}
              values={data.certificate_ids}
              onChange={(next) => setData({ certificate_ids: next })}
              addLabel={t("common.add") || "Aggiungi"}
              emptyLabel={t("common.empty") || "Nessun elemento aggiunto"}
              onAddClick={() => setInternalCertOpen(true)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MultiAssociationField
              label={t("fields.certificate-signature")}
              values={data.signature_certificate_ids}
              onChange={(next) => setData({ signature_certificate_ids: next })}
              addLabel={t("common.add") || "Aggiungi"}
              emptyLabel={t("common.empty") || "Nessun elemento aggiunto"}
              onAddClick={() => setSignatureCertOpen(true)}
            />
          </Grid>
          {canViewBadge && (
            <Grid item xs={12} md={6}>
              <AssociationFieldPlaceholder
                label={t("fields.badge")}
                value={data.badge_id}
                onChange={(next) => setData({ badge_id: next })}
                addLabel={t("common.add") || "Aggiungi"}
                emptyLabel={t("common.empty") || "Nessun elemento aggiunto"}
                onAddClick={() => canEditBadge && setBadgeOpen(true)}
                disabled={!canEditBadge}
              />
            </Grid>
          )}
        </Grid>

        <CertificatePickerCst
          open={internalCertOpen}
          onClose={() => setInternalCertOpen(false)}
          title={tCert("title-internal") || "Associa Certificato interno"}
          initialSelection={toSelections(data.certificate_ids)}
          onConfirm={(selection) => setData({ certificate_ids: selection })}
        />
        <CertificatePickerCst
          open={signatureCertOpen}
          onClose={() => setSignatureCertOpen(false)}
          title={tCert("title-signature") || "Associa Certificato con firma"}
          initialSelection={toSelections(data.signature_certificate_ids)}
          onConfirm={(selection) =>
            setData({ signature_certificate_ids: selection })
          }
        />
        {canViewBadge && (
          <BadgePickerCst
            open={badgeOpen}
            onClose={() => setBadgeOpen(false)}
            title={tBadge("title") || "Associa Badge"}
            initialSelection={
              data.badge_id
                ? {
                    id: Number(data.badge_id.id),
                    label: data.badge_id.label || "",
                  }
                : null
            }
            onConfirm={(selection) => setData({ badge_id: selection })}
          />
        )}
      </Box>

      <SectionDivider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionHeader
          title={t("sections.complexity.title")}
          description={t("sections.complexity.description") || undefined}
        />
        <SelectCst
          id="path-complexity"
          label={t("fields.complexity") || ""}
          value={data.complexity || ""}
          onChange={(v: any) => setData({ complexity: v as ComplexityLevel })}
          options={COMPLEXITY_OPTIONS.map((o) => ({
            value: o.value,
            label: t(o.i18nKey) || o.value,
          }))}
        />
      </Box>

      <SectionDivider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionHeader
          title={t("sections.score.title")}
          description={t("sections.score.description") || undefined}
        />
        <InputCst
          id="path-score"
          label={t("fields.score") || ""}
          value={data.score}
          onChange={(e: any) => setData({ score: e.target.value })}
          type="number"
          min={0}
        />
      </Box>

      <SectionDivider />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionHeader
          title={t("sections.credits.title")}
          description={t("sections.credits.description") || undefined}
        />
        <InputCst
          id="path-credits"
          label={t("fields.credits") || ""}
          value={data.credits}
          onChange={(e: any) => setData({ credits: e.target.value })}
          type="number"
          min={0}
          max={1000}
          error={!!errors.credits}
          errorMessage={errMsg("credits")}
        />
      </Box>

      <SectionDivider />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionHeader
          title={t("sections.reporting-tags.title")}
          description={t("sections.reporting-tags.description") || undefined}
        />
        <InputChipsCst
          id="path-lo-tag"
          label={t("fields.reporting-tags") || ""}
          value={data.lo_tag as unknown as []}
          onAdd={(chips: string[]) => setData({ lo_tag: chips })}
          maxLength={25}
        />
      </Box>
    </Box>
  );
};

export default AdditionalInfoStep;
